import { FC, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const SwitcherContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  @media (max-width: 722px) {
    margin-top: 2rem;
  }
`;

const SelectedLanguage = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 0.3rem 0.8rem;
  font-family: var(--secondary-font-family);
  font-size: 0.9rem;
  opacity: 0.7;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    opacity: 1;
    color: var(--primary-color);
    border-color: var(--primary-color);
  }

  .flag {
    font-size: 1.2em;
    line-height: 1;
  }

  .icon {
    font-size: 0.7rem;
    transition: transform 0.3s ease;
  }

  &[aria-expanded="true"] .icon {
    transform: rotate(180deg);
  }
`;

const LanguageList = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0.5rem;
  opacity: ${props => props.$isOpen ? '1' : '0'};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  z-index: 1000;
`;

const LanguageOption = styled.button<{ $active: boolean }>`
  width: 100%;
  background: transparent;
  border: none;
  color: ${props => props.$active ? 'var(--primary-color)' : '#fff'};
  padding: 0.5rem;
  font-family: var(--secondary-font-family);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    color: var(--primary-color);
    background: rgba(255, 255, 255, 0.1);
  }

  .flag {
    font-size: 1.2em;
    line-height: 1;
  }
`;

const LanguageSwitcher: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n, t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <SwitcherContainer ref={dropdownRef}>
      <SelectedLanguage 
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="flag">{i18n.language === 'fr' ? '🇫🇷' : '🇬🇧'}</span>
        {i18n.language.toUpperCase()}
        <FontAwesomeIcon icon={faChevronDown} className="icon" />
      </SelectedLanguage>
      <LanguageList $isOpen={isOpen}>
        <LanguageOption 
          onClick={() => changeLanguage('fr')}
          $active={i18n.language === 'fr'}
        >
          <span className="flag">🇫🇷</span>
          {t('language.fr')}
        </LanguageOption>
        <LanguageOption 
          onClick={() => changeLanguage('en')}
          $active={i18n.language === 'en'}
        >
          <span className="flag">🇬🇧</span>
          {t('language.en')}
        </LanguageOption>
      </LanguageList>
    </SwitcherContainer>
  );
};

export default LanguageSwitcher; 